import { createResource, type Match, type Query } from '@atlassian/react-resource-router';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { getProjectKeyId } from '@atlassian/jira-router-resources-utils/src/utils/get-project-key-id/index.tsx';

type IntegrationsStatus = {
	slackProjectConnected: boolean;
	teamsProjectConnected: boolean;
	slackHasLoggedIn: boolean;
	slackIntegrationEnabled: boolean;
	teamsIntegrationEnabled: boolean;
};

type ProjectSidebarAddonNavItem = {
	id: string;
	name: string;
	url?: string;
	links?: ProjectSidebarAddonNavItem[];
};

// Match on part of the ID that is consistent between prod and staging
const SLACK_PROJECT_PAGE_ID_PARTIAL =
	'com.atlassian.plugins.atlassian-connect-plugin:jira-slack-integration';
const TEAMS_PROJECT_PAGE_ID_PARTIAL = 'com.atlassian.plugins.atlassian-connect-plugin:msteams';

const getIdentifier = (match: Match, query: Query): string | undefined => {
	const { projectKey, projectId } = getProjectKeyId(match, query);
	return projectKey || projectId;
};

const fetchIntegrationEnabledStatuses = async (
	projectKeyOrId: string,
): Promise<{ slackIntegrationEnabled: boolean; teamsIntegrationEnabled: boolean }> => {
	try {
		// Using this until as a proxy until there is a more direct endpoint
		const addons = await performGetRequest<ProjectSidebarAddonNavItem[]>(
			`/rest/navigation/4/sidebar/project/${projectKeyOrId}/addons?type=settings`,
		);

		if (!addons || !addons.length) {
			return {
				slackIntegrationEnabled: false,
				teamsIntegrationEnabled: false,
			};
		}

		return {
			slackIntegrationEnabled: addons.some((i) => i.id.includes(SLACK_PROJECT_PAGE_ID_PARTIAL)),
			teamsIntegrationEnabled: addons.some((i) => i.id.includes(TEAMS_PROJECT_PAGE_ID_PARTIAL)),
		};
	} catch (err) {
		const normalizedError = err instanceof Error ? err : new Error(JSON.stringify(err));
		fireErrorAnalytics({
			meta: {
				id: 'levelUpSettings.resourceError',
				packageName: 'jiraProjectSettingsLevelUp',
				teamName: 'jlove-makkuro',
			},
			attributes: {
				action: 'fetchIntegrationEnabledStatuses',
			},
			error: normalizedError,
		});
		throw normalizedError;
	}
};

const fetchProjectConnectedStatuses = async (
	projectKeyOrId: string,
): Promise<Pick<IntegrationsStatus, 'slackProjectConnected' | 'teamsProjectConnected'>> => {
	try {
		// Using this until as a proxy until there is a more direct endpoint
		const addons = await performGetRequest<ProjectSidebarAddonNavItem[]>(
			`/rest/navigation/4/sidebar/project/${projectKeyOrId}/addons?type=home`,
		);

		if (!addons || !addons.length) {
			return {
				slackProjectConnected: false,
				teamsProjectConnected: false,
			};
		}

		return {
			slackProjectConnected: addons.some((i) => i.id.includes(SLACK_PROJECT_PAGE_ID_PARTIAL)),
			teamsProjectConnected: addons.some((i) => i.id.includes(TEAMS_PROJECT_PAGE_ID_PARTIAL)),
		};
	} catch (err) {
		const normalizedError = err instanceof Error ? err : new Error(JSON.stringify(err));
		fireErrorAnalytics({
			meta: {
				id: 'levelUpSettings.resourceError',
				packageName: 'jiraProjectSettingsLevelUp',
				teamName: 'jlove-makkuro',
			},
			attributes: {
				action: 'fetchProjectConnectedStatuses',
			},
			error: normalizedError,
		});
		throw normalizedError;
	}
};

export const softwareOnboardingGetStartedIntegrationsStatusResource =
	createResource<IntegrationsStatus | null>({
		type: 'JSW_ONBOARDING_GET_STARTED_INTEGRATIONS_STATUS_RESOURCE',
		getKey: ({ match, query }) => getIdentifier(match, query) ?? 'none',
		getData: async ({ match, query }) => {
			const projectKeyOrId = getIdentifier(match, query);
			if (projectKeyOrId == null) return null;

			const [
				{ slackProjectConnected, teamsProjectConnected },
				{ slackIntegrationEnabled, teamsIntegrationEnabled },
			] = await Promise.all([
				fetchProjectConnectedStatuses(projectKeyOrId),
				fetchIntegrationEnabledStatuses(projectKeyOrId),
			]);

			return {
				slackProjectConnected,
				teamsProjectConnected,
				// Mocking this value to false. No need to call API for slack login state for now, will be used for future experiment
				// Can remove this from being returned, when cleaning up the old experiment consumer https://hello.jira.atlassian.cloud/browse/KURO-10952
				slackHasLoggedIn: false,
				slackIntegrationEnabled,
				teamsIntegrationEnabled,
			};
		},
		maxAge: Number.MAX_SAFE_INTEGER,
		// For now, skip SSR for this resource since /gateway/api/slack/jira/userinfo is not available on SSR
		isBrowserOnly: true,
	});
